import {
  QuotationProject,
  QuotationItem,
  QuotationSystem,
  QuotationCalculations,
  QuotationParameters,
} from '../types';
import { logger } from '@/lib/logger';
import { computeLinePrice } from './linePricing';
import { bucketForLaborSubtype, type LaborBucket } from './laborBuckets';
import { resolveDiscountILS } from './quotationDiscount';

// ============ Display Number Generation ============
export function generateDisplayNumber(
  systemOrder: number,
  itemOrder: number
): string {
  return `${systemOrder}.${itemOrder}`;
}

// ============ Item Calculations ============
export function calculateItemTotals(
  item: QuotationItem,
  parameters: QuotationParameters
): QuotationItem {
  // Calculate total prices
  const totalPriceUSD = item.quantity * item.unitPriceUSD;
  const totalPriceILS = item.quantity * item.unitPriceILS;

  // Customer price comes from the single per-line pricing authority.
  const { customerPriceILS } = computeLinePrice(item, parameters);

  return {
    ...item,
    totalPriceUSD,
    totalPriceILS,
    customerPriceILS,
    displayNumber: generateDisplayNumber(item.systemOrder, item.itemOrder),
  };
}

// ============ System Totals ============
export interface SystemTotals {
  systemId: string;
  systemName: string;
  totalUSD: number;
  totalILS: number;
  hardwareUSD: number;
  hardwareILS: number;
  softwareUSD: number;
  softwareILS: number;
  laborUSD: number;
  laborILS: number;
  engineeringILS: number;
  integrationILS: number;
  developmentILS: number;
  installationILS: number;
  itemCount: number;
}

/** Accumulator key for a labor bucket, e.g. 'engineering' → 'engineeringILS'. */
const BUCKET_ACCUMULATOR_KEY = {
  engineering: 'engineeringILS',
  integration: 'integrationILS',
  development: 'developmentILS',
  installation: 'installationILS',
} as const satisfies Record<LaborBucket, keyof SystemTotals>;

export function calculateSystemTotals(
  system: QuotationSystem,
  items: QuotationItem[],
  parameters: QuotationParameters
): SystemTotals {
  const systemItems = items.filter(item => item.systemId === system.id);
  const systemQuantity = system.quantity || 1;

  const totals = systemItems.reduce(
    (acc, item) => {
      const calculatedItem = calculateItemTotals(item, parameters);

      acc.totalUSD += calculatedItem.totalPriceUSD;
      acc.totalILS += calculatedItem.totalPriceILS;
      acc.itemCount += 1;

      // Classify by item type (hardware, software, labor)
      switch (calculatedItem.itemType) {
        case 'hardware':
          acc.hardwareUSD += calculatedItem.totalPriceUSD;
          acc.hardwareILS += calculatedItem.totalPriceILS;
          break;
        case 'software':
          acc.softwareUSD += calculatedItem.totalPriceUSD;
          acc.softwareILS += calculatedItem.totalPriceILS;
          break;
        case 'labor': {
          acc.laborUSD += calculatedItem.totalPriceUSD;
          acc.laborILS += calculatedItem.totalPriceILS;

          // Roll up into the labor bucket. There is deliberately no `default`
          // here: the mapping is total over LaborSubtype, so an unhandled
          // subtype is a compile error in laborBuckets.ts rather than silent
          // arithmetic in this reducer. That silent default is what made every
          // subtype except three read as Engineering (#58).
          const bucket = bucketForLaborSubtype(calculatedItem.laborSubtype);
          acc[BUCKET_ACCUMULATOR_KEY[bucket]] += calculatedItem.totalPriceILS;
          break;
        }
      }

      return acc;
    },
    {
      totalUSD: 0,
      totalILS: 0,
      hardwareUSD: 0,
      hardwareILS: 0,
      softwareUSD: 0,
      softwareILS: 0,
      laborUSD: 0,
      laborILS: 0,
      engineeringILS: 0,
      integrationILS: 0,
      developmentILS: 0,
      installationILS: 0,
      itemCount: 0,
    }
  );

  // Multiply totals by system quantity
  return {
    systemId: system.id,
    systemName: system.name,
    totalUSD: totals.totalUSD * systemQuantity,
    totalILS: totals.totalILS * systemQuantity,
    hardwareUSD: totals.hardwareUSD * systemQuantity,
    hardwareILS: totals.hardwareILS * systemQuantity,
    softwareUSD: totals.softwareUSD * systemQuantity,
    softwareILS: totals.softwareILS * systemQuantity,
    laborUSD: totals.laborUSD * systemQuantity,
    laborILS: totals.laborILS * systemQuantity,
    engineeringILS: totals.engineeringILS * systemQuantity,
    integrationILS: totals.integrationILS * systemQuantity,
    developmentILS: totals.developmentILS * systemQuantity,
    installationILS: totals.installationILS * systemQuantity,
    itemCount: totals.itemCount,
  };
}

// ============ Project Calculations ============
export function calculateQuotationTotals(
  project: QuotationProject
): QuotationCalculations {
  const { items = [], systems = [], parameters } = project;

  // Validate inputs
  if (!parameters) {
    throw new Error('Quotation parameters are required for calculations');
  }

  // Calculate all items with markup
  const calculatedItems = items.map(item =>
    calculateItemTotals(item, parameters)
  );

  // Calculate system totals
  const systemTotals = systems.map(system =>
    calculateSystemTotals(system, calculatedItems, parameters)
  );

  // Aggregate cost totals (without markup)
  const costAggregates = systemTotals.reduce(
    (acc, system) => {
      acc.totalHardwareUSD += system.hardwareUSD;
      acc.totalHardwareILS += system.hardwareILS;
      acc.totalSoftwareUSD += system.softwareUSD;
      acc.totalSoftwareILS += system.softwareILS;
      acc.totalLaborUSD += system.laborUSD;
      acc.totalLaborILS += system.laborILS;
      acc.totalEngineeringILS += system.engineeringILS;
      acc.totalIntegrationILS += system.integrationILS;
      acc.totalDevelopmentILS += system.developmentILS;
      acc.totalInstallationILS += system.installationILS;
      acc.subtotalUSD += system.totalUSD;
      acc.subtotalILS += system.totalILS;
      return acc;
    },
    {
      totalHardwareUSD: 0,
      totalHardwareILS: 0,
      totalSoftwareUSD: 0,
      totalSoftwareILS: 0,
      totalLaborUSD: 0,
      totalLaborILS: 0,
      totalEngineeringILS: 0,
      totalIntegrationILS: 0,
      totalDevelopmentILS: 0,
      totalInstallationILS: 0,
      subtotalUSD: 0,
      subtotalILS: 0,
    }
  );

  // Total cost of all lines (hardware/software/labor at cost)
  const totalCostILS = costAggregates.subtotalILS;

  // Per-line customer price sum — the single source of truth for revenue.
  // (calculateItemTotals already priced each item via computeLinePrice.)
  const totalCustomerPriceILS = systems.reduce((total, system) => {
    const systemItems = calculatedItems.filter(
      item => item.systemId === system.id
    );
    const systemCustomerPrice = systemItems.reduce(
      (sum, item) => sum + item.customerPriceILS,
      0
    );
    return total + systemCustomerPrice * (system.quantity || 1);
  }, 0);

  // Profit (רווח) = sum of per-line profit = customer price − cost.
  const totalProfitILS = totalCustomerPriceILS - totalCostILS;

  // Risk addition applies to the whole customer price (= cost + profit).
  const riskAdditionILS =
    totalCustomerPriceILS * ((parameters.riskPercent || 0) / 100);

  // Total before discount and VAT.
  const totalQuoteILS = totalCustomerPriceILS + riskAdditionILS;

  // #77: the customer discount, after risk and before VAT. `resolveDiscountILS`
  // returns 0 for every quotation that has no discount set, which is what keeps
  // the three lines below identical to what they computed before this feature —
  // and what the pinned math fixtures assert.
  const discountILS = resolveDiscountILS(totalQuoteILS, parameters);
  const netQuoteILS = totalQuoteILS - discountILS;

  // Calculate VAT — on the discounted total, so the printed VAT figure is the
  // VAT actually charged.
  const totalVATILS = parameters.includeVAT
    ? netQuoteILS * (parameters.vatRate / 100)
    : 0;
  const finalTotalILS = netQuoteILS + totalVATILS;

  // Calculate profit margin: (profit + risk − discount) / total before VAT.
  // The discount comes out of profit, so it belongs in the numerator; leaving
  // it out would report a margin the company is not earning.
  const profitMarginPercent =
    netQuoteILS > 0
      ? ((totalProfitILS + riskAdditionILS - discountILS) / netQuoteILS) * 100
      : 0;

  return {
    totalHardwareUSD: costAggregates.totalHardwareUSD,
    totalHardwareILS: costAggregates.totalHardwareILS,
    totalSoftwareUSD: costAggregates.totalSoftwareUSD,
    totalSoftwareILS: costAggregates.totalSoftwareILS,
    totalLaborUSD: costAggregates.totalLaborUSD,
    totalLaborILS: costAggregates.totalLaborILS,
    totalEngineeringILS: costAggregates.totalEngineeringILS,
    totalIntegrationILS: costAggregates.totalIntegrationILS,
    totalDevelopmentILS: costAggregates.totalDevelopmentILS,
    totalInstallationILS: costAggregates.totalInstallationILS,
    subtotalUSD: costAggregates.subtotalUSD,
    subtotalILS: costAggregates.subtotalILS,
    totalCustomerPriceILS,
    riskAdditionILS,
    totalQuoteILS,
    discountILS,
    netQuoteILS,
    totalVATILS,
    finalTotalILS,
    totalCostILS,
    totalProfitILS,
    profitMarginPercent,
  };
}

// ============ Labor Days (#56) ============

export interface LaborDays {
  /** Days performed in-house. */
  internal: number;
  /** Days bought from an outsourced supplier. */
  external: number;
  /** internal + external. */
  total: number;
}

/**
 * Count the work days a quotation represents, split internal vs outsourced.
 *
 * A labor line's `quantity` IS its day count and its `unitPrice` is the daily
 * rate (see the labor branch of `addLaborItem`), so days are summed directly
 * rather than divided back out of the labor cost. That division —
 * `totalLaborILS / dayWorkCost`, which `calculateLaborMetrics` still does for
 * the cross-quotation analytics view — is only correct when every labor line
 * bills at the internal rate. One outsourced line at a different rate and it
 * silently reports the wrong number of days.
 *
 * Two invariants are deliberate, both so this figure can never disagree with
 * the labor ₪ line it is rendered beneath:
 *
 *  - Internal vs external is decided by `isInternalLabor === false`, the exact
 *    test `computeLinePrice` uses. `undefined` means internal in both places.
 *  - Days are multiplied by their system's quantity, and items whose systemId
 *    matches no system are skipped — mirroring `calculateSystemTotals`, which
 *    only ever sees items reached through a system.
 */
export function calculateLaborDays(
  items: QuotationItem[],
  systems: QuotationSystem[]
): LaborDays {
  const systemQuantities = new Map(
    systems.map(system => [system.id, system.quantity || 1])
  );

  let internal = 0;
  let external = 0;

  for (const item of items) {
    if (item.itemType !== 'labor') continue;

    const systemQuantity = systemQuantities.get(item.systemId);
    if (systemQuantity === undefined) continue;

    const days = Number.isFinite(item.quantity) ? item.quantity : 0;
    const scaled = days * systemQuantity;

    if (item.isInternalLabor === false) {
      external += scaled;
    } else {
      internal += scaled;
    }
  }

  return { internal, external, total: internal + external };
}

/**
 * Render a day count for display: one decimal place, with a trailing `.0`
 * trimmed so whole days read as "12" and halves as "12.5".
 */
export function formatLaborDays(days: number): string {
  if (!Number.isFinite(days)) return '0';
  const rounded = Number(days.toFixed(1));
  // Round-tripping through Number drops the trailing zero for free, and folds
  // -0 (which a tiny negative would otherwise render as "-0") back to 0.
  return rounded === 0 ? '0' : String(rounded);
}

// ============ System Renumbering ============
/**
 * Renumber systems to be sequential (1, 2, 3...)
 * This ensures numbering is always continuous even after deletions
 */
export function renumberSystems(systems: QuotationSystem[]): QuotationSystem[] {
  return systems.map((system, index) => ({
    ...system,
    order: index + 1,
  }));
}

// ============ Drag-and-drop reorder ============

/**
 * A simplified projection of grid rows that the drag-drop walker needs to see.
 * Mirrors the shape produced by AG Grid's forEachNode for the quotation grid:
 * a sequence of system-header rows interleaved with item rows.
 */
export interface DragDropRow {
  isSystemGroup?: boolean;
  systemId?: string;
  id?: string;
}

/**
 * Walks the post-drag visual order of grid rows and produces the new items list,
 * with each item's systemId set to the system header that precedes it and
 * itemOrder set to its position within that system (1-based).
 *
 * Items that appear before any system header are skipped — they have no valid
 * parent and would otherwise lose their systemId. Drag handles are gated on
 * isSystemGroup at the column level, so system rows themselves should never
 * move; this function trusts that invariant and uses every isSystemGroup row
 * as a section divider.
 */
export function computeReorderedItemsFromVisualRows(
  visualRows: DragDropRow[],
  originalItems: QuotationItem[]
): QuotationItem[] {
  const itemMap = new Map(originalItems.map(i => [i.id, i]));

  let currentSystemId: string | null = null;
  let currentItemOrder = 0;
  const reordered: QuotationItem[] = [];

  for (const row of visualRows) {
    if (row.isSystemGroup) {
      currentSystemId = row.systemId ?? null;
      currentItemOrder = 0;
      continue;
    }
    if (!currentSystemId || !row.id) continue;

    const original = itemMap.get(row.id);
    if (!original) continue;

    currentItemOrder += 1;
    reordered.push({
      ...original,
      systemId: currentSystemId,
      itemOrder: currentItemOrder,
    });
  }

  return reordered;
}

// ============ Item Renumbering ============
export function renumberItems(
  items: QuotationItem[],
  systems?: { id: string; order: number }[]
): QuotationItem[] {
  // Create systemId to order mapping if systems provided
  const systemOrderMap: Record<string, number> = {};
  if (systems) {
    systems.forEach(system => {
      systemOrderMap[system.id] = system.order;
    });
  }

  // Group items by system
  const itemsBySystem = items.reduce(
    (acc, item) => {
      if (!acc[item.systemId]) {
        acc[item.systemId] = [];
      }
      acc[item.systemId].push(item);
      return acc;
    },
    {} as Record<string, QuotationItem[]>
  );

  // Sort items within each system and renumber
  const renumberedItems: QuotationItem[] = [];

  Object.keys(itemsBySystem).forEach(systemId => {
    // Get the correct system order (either from map or from first item)
    const systemOrder = systems
      ? systemOrderMap[systemId]
      : itemsBySystem[systemId][0]?.systemOrder || 1;

    const systemItems = itemsBySystem[systemId]
      .sort((a, b) => a.itemOrder - b.itemOrder)
      .map((item, index) => ({
        ...item,
        systemOrder: systemOrder, // Update system order
        itemOrder: index + 1,
        displayNumber: generateDisplayNumber(systemOrder, index + 1),
      }));

    renumberedItems.push(...systemItems);
  });

  return renumberedItems;
}

// #25: Pure helper for system reorder. Given the current systems/items
// and a new top-to-bottom system ordering (by id), returns the updated
// systems (with order=index+1, gap-free) and items (with refreshed
// systemOrder + displayNumber via renumberItems).
//
// Returns null if newSystemOrder is invalid (different length, or contains
// ids not present in systems). Items keep their systemId — they follow
// their parent system to its new position.
export function applySystemReorder(
  systems: QuotationSystem[],
  items: QuotationItem[],
  newSystemOrder: string[]
): { systems: QuotationSystem[]; items: QuotationItem[] } | null {
  if (newSystemOrder.length !== systems.length) return null;

  const updatedSystems: QuotationSystem[] = [];
  for (let i = 0; i < newSystemOrder.length; i++) {
    const id = newSystemOrder[i];
    const sys = systems.find(s => s.id === id);
    if (!sys) return null;
    updatedSystems.push({ ...sys, order: i + 1 });
  }

  const renumberedItems = renumberItems(
    items,
    updatedSystems.map(s => ({ id: s.id, order: s.order }))
  );

  return { systems: updatedSystems, items: renumberedItems };
}

// #138: Pure helper for "the user dragged system X and dropped it on system Y".
//
// #25 read the new ordering back out of AG Grid with `api.forEachNode`, which
// cannot work here: the system drag narrows the grid's `rowData` to headers
// only at `rowDragEnter`, and AG Grid 32's `ClientSideNodeManager.setRowData`
// rebuilds every RowNode from that array. The managed drag is still holding
// the node it captured at `onDragEnter` and commits moves by object identity
// (`ensureRowsAtPixel` -> `_removeFromArray`, which silently does nothing on a
// miss), so from that moment the grid is moving an orphan. What it reports at
// drag end is either the untouched order or that same list with the orphan
// spliced back in — never the order the user just dragged to.
//
// The drop target, by contrast, is computed fresh when the event is dispatched
// (`draggingToRowDragEvent` calls `getRowIndexAtPixel` at that instant), so
// `event.node` / `event.overNode` survive the rebuild. This turns those two
// ids into the new ordering without consulting the grid's row model at all.
//
// `targetSystemId` undefined means the row was dropped past the last one
// (AG Grid reports `overIndex: -1` and no `overNode`), which sends the dragged
// system to the bottom.
//
// Returns undefined when there is nothing to do — the dragged id is unknown,
// the target is unknown, or the result is the order we already had — so the
// caller can skip the state write and the persistence round trip entirely.
export function moveSystemInOrder(
  currentOrder: string[],
  draggedSystemId: string,
  targetSystemId?: string
): string[] | undefined {
  const from = currentOrder.indexOf(draggedSystemId);
  if (from === -1) return undefined;

  // Dropped past the last row: the dragged system goes to the bottom.
  const to =
    targetSystemId === undefined
      ? currentOrder.length - 1
      : currentOrder.indexOf(targetSystemId);
  if (to === -1 || to === from) return undefined;

  const next = [...currentOrder];
  next.splice(from, 1);
  // Removing `from` shifts everything after it down by one, which is exactly
  // what makes a single splice correct in both directions: dragging down lands
  // the system *after* its target, dragging up lands it *before*.
  next.splice(to, 0, draggedSystemId);

  return next;
}

// ============ Currency Conversion ============
export function convertUSDtoILS(
  usdAmount: number,
  exchangeRate: number
): number {
  return usdAmount * exchangeRate;
}

export function convertEURtoILS(
  eurAmount: number,
  exchangeRate: number
): number {
  return eurAmount * exchangeRate;
}

// ============ Default Parameters ============

/**
 * Get default quotation parameters from cached settings (synchronous)
 * Falls back to hard-coded defaults if settings not found
 */
export function getDefaultQuotationParameters(): QuotationParameters {
  try {
    // Pricing settings are team-scoped. Prefer the team cache populated by
    // TeamContext on mount; fall back to the legacy global cache for callers
    // that run before a team is set or for personal (non-team) settings.
    const teamId = localStorage.getItem('cpq_last_team_id');
    const cacheKeys = teamId
      ? [`cpq-settings-cache-${teamId}`, 'cpq-settings-cache']
      : ['cpq-settings-cache'];

    for (const key of cacheKeys) {
      const cache = localStorage.getItem(key);
      if (!cache) continue;
      const parsed = JSON.parse(cache);
      if (parsed.pricing) {
        const pricing = parsed.pricing;
        return {
          usdToIlsRate: pricing.usdToIlsRate ?? 3.7,
          eurToIlsRate: pricing.eurToIlsRate ?? 4.0,
          markupPercent: pricing.defaultMarkup ?? 0.75,
          outsourcedLaborMarkupPercent:
            pricing.defaultOutsourcedLaborMarkup ?? 0.75,
          dayWorkCost: pricing.dayWorkCost ?? 1200,
          profitPercent: 20, // Not configurable yet
          riskPercent: pricing.defaultRisk ?? 10,
          paymentTerms: '30 יום מהחשבונית',
          deliveryTime: pricing.deliveryTime ?? '4-6 שבועות',
          // #153: the business does not charge VAT, so no quotation is created with it
          // on. `includeVAT` stays on the parameters type rather than being deleted --
          // the field is persisted in exports and import fixtures, and removing it is a
          // wider change than this one. What changed is that nothing turns it on: these
          // are the only three places that ever set it, and there is no per-quotation
          // VAT field and no setting in Pricing Settings. It had simply always been true.
          //
          // After #148 the quotation screens all read netQuoteILS, so nothing the user
          // saw on a quotation included VAT. The analytics dashboard was the exception:
          // every revenue, pipeline and margin figure reads finalTotalILS, which was
          // netQuoteILS plus 17%. Those figures drop by that much here, which is the
          // correction, not a regression.
          includeVAT: false,
          vatRate: pricing.vatRate ?? 17,
        };
      }
    }
  } catch (error) {
    logger.error('Error loading pricing settings from cache:', error);
  }

  // Fallback to hard-coded defaults
  return {
    usdToIlsRate: 3.7,
    eurToIlsRate: 4.0,
    markupPercent: 0.75,
    outsourcedLaborMarkupPercent: 0.75,
    dayWorkCost: 1200,
    profitPercent: 20,
    riskPercent: 10,
    paymentTerms: '30 יום מהחשבונית',
    deliveryTime: '4-6 שבועות',
    includeVAT: false,
    vatRate: 17,
  };
}

/**
 * Load default quotation parameters from Supabase (async)
 * Use this when initializing the app or creating new quotations
 * @param teamId - Optional team ID for team-scoped settings
 */
export async function loadDefaultQuotationParameters(
  teamId?: string
): Promise<QuotationParameters> {
  try {
    const { loadSetting } = await import('../services/settingsService');
    const result = await loadSetting<{
      usdToIlsRate: number;
      eurToIlsRate: number;
      defaultMarkup: number;
      defaultOutsourcedLaborMarkup?: number;
      defaultRisk: number;
      dayWorkCost: number;
      vatRate: number;
      deliveryTime: string;
    }>('pricing', teamId); // ✅ Pass teamId to loadSetting

    if (result.success && result.data) {
      const pricing = result.data;
      return {
        usdToIlsRate: pricing.usdToIlsRate,
        eurToIlsRate: pricing.eurToIlsRate,
        markupPercent: pricing.defaultMarkup,
        outsourcedLaborMarkupPercent:
          pricing.defaultOutsourcedLaborMarkup ?? 0.75,
        dayWorkCost: pricing.dayWorkCost,
        profitPercent: 20, // Not configurable yet
        riskPercent: pricing.defaultRisk,
        paymentTerms: '30 יום מהחשבונית',
        deliveryTime: pricing.deliveryTime,
        includeVAT: false,
        vatRate: pricing.vatRate,
      };
    }
  } catch (error) {
    logger.error('Error loading pricing settings from Supabase:', error);
  }

  // Fallback to synchronous defaults
  return getDefaultQuotationParameters();
}

// ============ Validation ============
export function validateQuotationItem(item: Partial<QuotationItem>): string[] {
  const errors: string[] = [];

  if (!item.componentName || item.componentName.trim() === '') {
    errors.push('Item name is required');
  }

  if (!item.quantity || item.quantity <= 0) {
    errors.push('Quantity must be greater than 0');
  }

  if (!item.unitPriceUSD || item.unitPriceUSD < 0) {
    errors.push('USD unit price must be non-negative');
  }

  if (!item.unitPriceILS || item.unitPriceILS < 0) {
    errors.push('ILS unit price must be non-negative');
  }

  if (item.itemMarkupPercent !== undefined && item.itemMarkupPercent < 0) {
    errors.push('Markup percent cannot be negative');
  }

  return errors;
}

export function validateQuotationParameters(
  parameters: Partial<QuotationParameters>
): string[] {
  const errors: string[] = [];

  if (parameters.usdToIlsRate !== undefined && parameters.usdToIlsRate <= 0) {
    errors.push('USD to ILS exchange rate must be positive');
  }

  if (parameters.eurToIlsRate !== undefined && parameters.eurToIlsRate <= 0) {
    errors.push('EUR to ILS exchange rate must be positive');
  }

  if (parameters.dayWorkCost !== undefined && parameters.dayWorkCost < 0) {
    errors.push('Day work cost cannot be negative');
  }

  if (parameters.riskPercent !== undefined && parameters.riskPercent < 0) {
    errors.push('Risk percent cannot be negative');
  }

  // #77: a negative discount is a surcharge wearing a discount's name, and
  // there is no UI for one. Note there is deliberately no upper bound —
  // an over-100% discount is allowed and clamps to a zero total.
  if (parameters.discountValue !== undefined && parameters.discountValue < 0) {
    errors.push('Discount cannot be negative');
  }

  if (
    parameters.discountValue !== undefined &&
    parameters.discountValue > 0 &&
    parameters.discountType === undefined
  ) {
    errors.push('Discount type is required when a discount amount is set');
  }

  return errors;
}

// ============ Formatting Utilities ============
export function formatCurrency(
  amount: number,
  currency: 'USD' | 'ILS' | 'EUR'
): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return formatter.format(amount);
}

export function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

export function formatNumber(value: number, decimals: number = 2): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}
